import fs from 'node:fs'
import as from 'ansis'
import { Command } from 'commander'

import {
  parseConfig,
  type VersionedScalarConfig,
} from '@scalar-org/scalar-config'

import { output } from '@/helpers/std-out'

import { findConfig } from '../helpers'

/**
 * Validates a scalar config file and returns the validation result
 */
export function readAndParseConfig(configPath: string):
  | {
      success: true
      filePath: string
      validationTime: number
      config: VersionedScalarConfig
    }
  | {
      success: false
      error:
        | { type: 'file-not-found'; message: string; inputPath: string }
        | { type: 'parse-error'; message: string; filePath: string }
        | {
            type: 'validation-error'
            message: string
            filePath: string
            issues: any[]
          }
    } {
  const startTime = performance.now()

  // Read file
  const file = findConfig(configPath)

  // Check if configuration file was found
  if (!file) {
    return {
      success: false,
      error: {
        type: 'file-not-found',
        message: 'Could not find the Scalar Configuration file',
        inputPath: configPath,
      },
    }
  }

  const fileContent = fs.readFileSync(file, 'utf-8')

  const parseConfigResult = parseConfig(fileContent)

  if (!parseConfigResult.success) {
    return {
      success: false,
      error: {
        type: 'parse-error',
        message: 'Could not parse the Scalar Configuration file',
        filePath: file,
      },
    }
  }

  return {
    success: true,
    filePath: file,
    validationTime: Math.round(performance.now() - startTime),
    config: parseConfigResult.config,
  }
}

/**
 * Lint users scalar configs (scalar.config.json files)
 */
export function CheckConfigCommand() {
  const cmd = new Command('check-config')

  cmd.description('Check a Scalar Configuration file')
  cmd.argument('[file]', 'File to check')
  cmd.action(async (inputArgument: string) => {
    const result = readAndParseConfig(inputArgument)

    if (result.success) {
      output
        .info()
        .line(
          `${as.green('[SUCCESS]')} ${as.green('The Scalar Configuration is valid:')} ${as.green(result.filePath)}`,
        )
        .line(
          `${as.green('Scalar Configuration validated')} ${as.grey(
            `in ${as.white.bold(`${result.validationTime} ms`)}`,
          )}\n`,
        )
        .print()
    } else {
      const error = result.error
      if (error.type === 'file-not-found') {
        return output
          .error()
          .title('Invalid configuration file')
          .message('Could not find the Scalar Configuration file:')
          .message(error.inputPath)
          .exit('error')
      }

      if (error.type === 'parse-error') {
        return output
          .error()
          .title('Invalid configuration file')
          .line('Could not parse the Scalar Configuration file:')
          .message(error.filePath)
          .exit('error')
      }

      if (error.type === 'validation-error') {
        output
          .error()
          .title('The given Scalar Configuration is invalid.')
          .message(error.filePath)
          .print()

        if (error.issues) {
          output
            .error()
            .table(
              error.issues.map((issue) => [
                as.yellow(`${issue.path.join('/').trim() || 'root'}:`),
                issue.message,
              ]),
              { padding: 2 },
            )
            .print()
        }
        return output.error().exit('error')
      }
    }
  })

  return cmd
}
